import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { formatApiError } from "../utils/formatApiError";

import {
  getUsers,
  getUserDetail,
  getUserStatistics,
  createUser,
  updateUser,
  activateUser,
  deactivateUser,
  verifyUserEmail,
  deleteUser,
  getAvailableRoles,
  unlockUser,
  adminResetPassword,
  getUserSessions,
  revokeAllSessions,
  revokeSession,
  getUserAddresses,
  createUserAddress,
  updateUserAddress,
  deleteUserAddress,
  setDefaultAddress,
  getUserLoginHistory,
  bulkActivateUsers,
  bulkDeactivateUsers,
  bulkAssignRole,
  bulkRemoveRole,
} from "../services/adminUserService";
import {
  AdminUser,
  UserFilters,
  CreateUserRequest,
  UpdateUserRequest,
  UserStatistics,
  AvailableRole,
  AdminResetPasswordRequest,
  CreateUserAddressRequest,
  UpdateUserAddressRequest,
  BulkUserActionRequest,
  BulkRoleAssignmentRequest,
} from "../types/admin";

// Query Keys for consistent caching
export const adminUserKeys = {
  all: ["admin-users"] as const,
  users: (filters?: UserFilters) =>
    [...adminUserKeys.all, "list", filters] as const,
  user: (id: string) => [...adminUserKeys.all, "detail", id] as const,
  statistics: () => [...adminUserKeys.all, "statistics"] as const,
  roles: () => [...adminUserKeys.all, "roles"] as const,
  sessions: (id: string) => [...adminUserKeys.all, "sessions", id] as const,
  addresses: (id: string) => [...adminUserKeys.all, "addresses", id] as const,
  loginHistory: (id: string, page?: number) =>
    [...adminUserKeys.all, "login-history", id, page] as const,
};

// Hook for fetching users with filters
export const useUsers = (filters: UserFilters) => {
  return useQuery({
    queryKey: adminUserKeys.users(filters),
    queryFn: () => getUsers(filters),
    staleTime: 30000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes (formerly cacheTime)
  });
};

// Hook for fetching user statistics
export const useUserStatistics = () => {
  return useQuery({
    queryKey: adminUserKeys.statistics(),
    queryFn: getUserStatistics,
    staleTime: 60000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook for fetching user details
export const useUserDetail = (userId: string, enabled = true) => {
  return useQuery({
    queryKey: adminUserKeys.user(userId),
    queryFn: () => getUserDetail(userId),
    enabled: enabled && !!userId,
    staleTime: 30000, // 30 seconds
  });
};

// Hook for fetching available roles
export const useAvailableRoles = () => {
  return useQuery({
    queryKey: adminUserKeys.roles(),
    queryFn: getAvailableRoles,
    staleTime: 5 * 60 * 1000, // 5 minutes - roles don't change often
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook for fetching user sessions
export const useUserSessions = (userId: string, enabled = true) => {
  return useQuery({
    queryKey: adminUserKeys.sessions(userId),
    queryFn: () => getUserSessions(userId),
    enabled: enabled && !!userId,
    staleTime: 15000,
  });
};

// Hook for fetching user addresses
export const useUserAddresses = (userId: string, enabled = true) => {
  return useQuery({
    queryKey: adminUserKeys.addresses(userId),
    queryFn: () => getUserAddresses(userId),
    enabled: enabled && !!userId,
    staleTime: 30000,
  });
};

// Hook for fetching login history
export const useUserLoginHistory = (
  userId: string,
  page = 1,
  pageSize = 20,
  enabled = true,
) => {
  return useQuery({
    queryKey: adminUserKeys.loginHistory(userId, page),
    queryFn: () => getUserLoginHistory(userId, page, pageSize),
    enabled: enabled && !!userId,
    staleTime: 30000,
  });
};

// Hook for user mutations (create, update, delete, etc.)
export const useUserMutations = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: createUser,
    onSuccess: () => {
      // Invalidate and refetch users list and statistics
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
    },
    onError: (error) => {
      console.error("Error creating user:", error);
    },
  });

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: string;
      data: UpdateUserRequest;
    }) => updateUser(userId, data),
    onSuccess: (data, variables) => {
      // Invalidate users list and specific user detail
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
      queryClient.invalidateQueries({
        queryKey: adminUserKeys.user(variables.userId),
      });
    },
    onError: (error) => {
      console.error("Error updating user:", error);
    },
  });

  // Activate user mutation
  const activateUserMutation = useMutation({
    mutationFn: activateUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
    },
    onError: (error) => {
      console.error("Error activating user:", error);
    },
  });

  // Deactivate user mutation
  const deactivateUserMutation = useMutation({
    mutationFn: deactivateUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
    },
    onError: (error) => {
      console.error("Error deactivating user:", error);
    },
  });

  // Verify user email mutation
  const verifyEmailMutation = useMutation({
    mutationFn: verifyUserEmail,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
    },
    onError: (error) => {
      console.error("Error verifying user email:", error);
    },
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
    },
    onError: (error) => {
      console.error("Error deleting user:", error);
    },
  });

  // Unlock user mutation
  const unlockUserMutation = useMutation({
    mutationFn: unlockUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
    },
    onError: (error) => {
      console.error("Error unlocking user:", error);
    },
  });

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: string;
      data: AdminResetPasswordRequest;
    }) => adminResetPassword(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
    },
    onError: (error) => {
      console.error("Error resetting password:", error);
    },
  });



  // Revoke single session mutation
  const revokeSessionMutation = useMutation({
    mutationFn: ({
      userId,
      sessionId,
    }: {
      userId: string;
      sessionId: string;
    }) => revokeSession(userId, sessionId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: adminUserKeys.sessions(variables.userId),
      });
    },
    onError: (error) => {
      console.error("Error revoking session:", error);
    },
  });
  // Revoke all sessions mutation
  const revokeAllSessionsMutation = useMutation({
    mutationFn: revokeAllSessions,
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({
        queryKey: adminUserKeys.sessions(userId),
      });
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
    },
    onError: (error) => {
      console.error("Error revoking sessions:", error);
    },
  });
  // Bulk operations mutations
  const bulkActivateMutation = useMutation({
    mutationFn: bulkActivateUsers,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
      toast.success(t("admin.users.bulkActivateSuccess"));
    },
    onError: (error) => {
      toast.error(formatApiError(error));
    },
  });

  const bulkDeactivateMutation = useMutation({
    mutationFn: bulkDeactivateUsers,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
      toast.success(t("admin.users.bulkDeactivateSuccess"));
    },
    onError: (error) => {
      toast.error(formatApiError(error));
    },
  });

  const bulkAssignRoleMutation = useMutation({
    mutationFn: bulkAssignRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
      toast.success(t("admin.users.bulkAssignRoleSuccess"));
    },
    onError: (error) => {
      toast.error(formatApiError(error));
    },
  });

  const bulkRemoveRoleMutation = useMutation({
    mutationFn: bulkRemoveRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
      toast.success(t("admin.users.bulkRemoveRoleSuccess"));
    },
    onError: (error) => {
      toast.error(formatApiError(error));
    },
  });

  return {
    createUser: createUserMutation,
    updateUser: updateUserMutation,
    activateUser: activateUserMutation,
    deactivateUser: deactivateUserMutation,
    verifyEmail: verifyEmailMutation,
    deleteUser: deleteUserMutation,
    unlockUser: unlockUserMutation,
    resetPassword: resetPasswordMutation,
    revokeAllSessions: revokeAllSessionsMutation,
    revokeSession: revokeSessionMutation,
    bulkActivate: bulkActivateMutation,
    bulkDeactivate: bulkDeactivateMutation,
    bulkAssignRole: bulkAssignRoleMutation,
    bulkRemoveRole: bulkRemoveRoleMutation,
  };
};

// Hook for user address mutations
export const useUserAddressMutations = (userId: string) => {
  const queryClient = useQueryClient();

  const createAddressMutation = useMutation({
    mutationFn: (data: CreateUserAddressRequest) =>
      createUserAddress(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminUserKeys.addresses(userId),
      });
    },
  });

  const updateAddressMutation = useMutation({
    mutationFn: ({
      addressId,
      data,
    }: {
      addressId: string;
      data: UpdateUserAddressRequest;
    }) => updateUserAddress(userId, addressId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminUserKeys.addresses(userId),
      });
    },
  });

  const deleteAddressMutation = useMutation({
    mutationFn: (addressId: string) => deleteUserAddress(userId, addressId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminUserKeys.addresses(userId),
      });
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: (addressId: string) => setDefaultAddress(userId, addressId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: adminUserKeys.addresses(userId),
      });
    },
  });

  return {
    createAddress: createAddressMutation,
    updateAddress: updateAddressMutation,
    deleteAddress: deleteAddressMutation,
    setDefault: setDefaultMutation,
  };
};

// Hook for optimistic user updates (for immediate UI feedback)
export const useOptimisticUserUpdate = () => {
  const queryClient = useQueryClient();

  const updateUserOptimistically = (
    userId: string,
    updates: Partial<AdminUser>,
  ) => {
    // Update all user queries optimistically
    queryClient.setQueriesData(
      { queryKey: adminUserKeys.all },
      (oldData: any) => {
        if (!oldData?.data?.users) return oldData;

        return {
          ...oldData,
          data: {
            ...oldData.data,
            users: oldData.data.users.map((user: AdminUser) =>
              user.id === userId ? { ...user, ...updates } : user,
            ),
          },
        };
      },
    );

    // Update specific user detail query
    queryClient.setQueryData(adminUserKeys.user(userId), (oldData: any) => {
      if (!oldData?.data) return oldData;
      return {
        ...oldData,
        data: { ...oldData.data, ...updates },
      };
    });
  };

  return { updateUserOptimistically };
};
